import React, { useMemo } from 'react';
import FeedbackCollector from '@atlaskit/feedback-collector';
import { NotificationTypes, successNotificationBuilder, useApplicationContext, useNotification } from '@css-xp/base';
import { useLocalStorage } from 'usehooks-ts';
import { getEnvironment, PRODUCTION } from '@atlassian/temp-shared';
import {
  RESEARCH_GROUP_URL,
  PROD_ENTRYPOINT_ID,
  NON_PROD_ENTRYPOINT_ID,
  SUMMARY_PLACEHOLDER_TEXT,
} from '@css-xp/ppc-constants';
import { helpLegalLinks } from '@css-xp/ppc-consent-hub';
import { getPartnerServiceDeskUrlForGenericHelp } from '@css-xp/ppc-navigation-partner-support-utils';

const getCanContactLabel = () => {
  return (
    <div>
      Atlassian can contact me about this feedback. See our{' '}
      <a data-testid="privacy-policy-link" href={helpLegalLinks.privacyPolicyUrl} target="_blank" rel="noreferrer">
        privacy policy
      </a>
    </div>
  );
};

const getResearchLabel = () => {
  return (
    <div>
      I’d like to help improve Atlassian products by joining the{' '}
      <a data-testid="research-group-link" href={RESEARCH_GROUP_URL} target="_blank" rel="noreferrer">
        Atlassian Research Group
      </a>
    </div>
  );
};

const getFeedbackPreamble = () => {
  return (
    <div>
      For help, contact{' '}
      <a href={getPartnerServiceDeskUrlForGenericHelp()} target="_blank" rel="noreferrer">
        Partner Service Desk
      </a>
    </div>
  );
};

export const InProductHelpFeedback = ({
  onCloseFeedbackWindow,
  isOpen,
}: {
  isOpen: boolean;
  onCloseFeedbackWindow: () => void;
}) => {
  const { sendSuccessNotification } = useNotification();

  const context = useApplicationContext();

  const notification = successNotificationBuilder()
    .isVersion(NotificationTypes.FLAG)
    .withId('feedback-collected-success')
    .withHeader('Thanks for your feedback')
    .withMessage(`Your feedback has been submitted.`)
    .withDismissAfter(5000)
    .build();

  const onSubmit = () => {
    sendSuccessNotification(notification);
  };

  const name = context.displayName;
  const email = context.email;
  const [txaId] = useLocalStorage(`${context?.atlassianAccountId}-selectedTxa`, '');
  const environment = getEnvironment();
  const ENTRYPOINT_ID = useMemo(() => {
    if (environment === PRODUCTION) {
      return PROD_ENTRYPOINT_ID;
    } else {
      return NON_PROD_ENTRYPOINT_ID;
    }
  }, [environment]);

  return isOpen ? (
    <FeedbackCollector
      entrypointId={ENTRYPOINT_ID}
      name={name}
      email={email}
      onClose={onCloseFeedbackWindow}
      onSubmit={onSubmit}
      shouldGetEntitlementDetails={false}
      submitButtonLabel="Send"
      summaryPlaceholder={SUMMARY_PLACEHOLDER_TEXT}
      feedbackTitle={'Give feedback'}
      feedbackTitleDetails={getFeedbackPreamble()}
      canBeContactedLabel={getCanContactLabel()}
      enrolInResearchLabel={getResearchLabel()}
      showTypeField={false}
      additionalFields={[{ id: 'customfield_10047', value: `Transaction Account Id: ${txaId}` }]}
    />
  ) : null;
};
